(() => {
  if (customElements.get('amani-fitting')) return;

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parts = (s) => {
    const d = new Date(`${s}T12:00:00Z`);
    return { day: DAYS[d.getUTCDay()], date: d.getUTCDate(), month: MONTHS[d.getUTCMonth()] };
  };
  const dayText = (s) => {
    const p = parts(s);
    return `${p.day} ${p.date} ${p.month}`;
  };
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const PHONE_RE = /^[+0-9 ()]{7,20}$/;

  class AmaniFitting extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.openedAt = Date.now();
      this.form = this.querySelector('[data-form]');
      this.later = this.querySelector('[data-later]');
      this.noSize = this.querySelector('[data-nosize]');
      this.daysBox = this.querySelector('[data-days]');
      this.timesBox = this.querySelector('[data-times]');
      this.loading = this.querySelector('[data-loading]');
      this.message = this.querySelector('[data-msg]');
      this.sendButton = this.querySelector('[data-send]');
      this.max = Number(this.dataset.max) || 3;
      this.slots = null;
      this.day = '';
      this.time = '';

      this.form?.addEventListener('change', (event) => this.changed(event), options);
      this.form?.addEventListener('input', () => {
        if (this.message?.classList.contains('is-error')) this.say('');
      }, options);
      this.form?.addEventListener('submit', (event) => this.submit(event), options);

      const wanted = new URLSearchParams(window.location.search).get('piece');
      if (wanted) {
        const box = this.form?.querySelector(`input[name="pieces"][data-handle="${CSS.escape(wanted)}"]`);
        if (box) box.checked = true;
      }
      this.limitPieces();
      this.load();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async load() {
      const api = this.dataset.api;
      if (!api) return this.down();
      try {
        const url = new URL(api);
        url.searchParams.set('fittings', '1');
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'omit', cache: 'no-store' });
        const data = await response.json();
        if (!data || !data.ok || !Array.isArray(data.days)) throw new Error('bad');
        this.slots = data;
        if (this.loading) this.loading.hidden = true;
        if (data.minutes) {
          const hint = this.querySelector('[data-when-hint]');
          if (hint) hint.textContent = `Monday to Friday. A fitting takes about ${data.minutes} minutes.`;
        }
        this.renderDays();
        this.checkSize();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.down();
      }
    }

    down() {
      if (this.loading) this.loading.hidden = true;
      const down = this.querySelector('[data-down]');
      if (down) down.hidden = false;
      if (this.later) this.later.hidden = true;
    }

    taken(day, time) {
      return !!this.slots && this.slots.taken.indexOf(`${day} ${time}`) !== -1;
    }

    openTimes(day) {
      if (!this.slots) return [];
      return this.slots.times.filter((t) => !this.taken(day, t));
    }

    renderDays() {
      if (!this.daysBox || !this.slots) return;
      this.daysBox.replaceChildren();
      const days = this.slots.days || [];
      if (this.day && (days.indexOf(this.day) === -1 || !this.openTimes(this.day).length)) this.day = '';
      days.forEach((d) => {
        const p = parts(d);
        const full = !this.openTimes(d).length;
        const button = make('button', 'amf__day');
        button.type = 'button';
        button.dataset.day = d;
        button.setAttribute('aria-pressed', String(d === this.day));
        button.disabled = full;
        button.append(make('span', 'amf__day-name', p.day), make('span', 'amf__day-num', String(p.date)), make('span', 'amf__day-month', full ? 'Full' : p.month));
        button.setAttribute('aria-label', `${dayText(d)}${full ? ', fully booked' : ''}`);
        button.addEventListener('click', () => {
          this.day = d;
          if (this.time && this.taken(d, this.time)) this.time = '';
          this.renderDays();
          this.say('');
        });
        this.daysBox.append(button);
      });
      this.renderTimes();
    }

    renderTimes() {
      if (!this.timesBox || !this.slots) return;
      this.timesBox.replaceChildren();
      if (!this.day) {
        this.timesBox.append(make('p', 'amf__hint', 'Pick a day to see the times.'));
        return;
      }
      this.slots.times.forEach((t) => {
        const label = make('label', 'amf__pill');
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = 'time';
        input.value = t;
        input.checked = t === this.time;
        input.disabled = this.taken(this.day, t);
        input.addEventListener('change', () => {
          this.time = t;
          this.say('');
        });
        const span = make('span', null, t);
        if (input.disabled) span.setAttribute('aria-label', `${t}, booked`);
        label.append(input, span);
        this.timesBox.append(label);
      });
    }

    changed(event) {
      const target = event.target;
      if (target.name === 'pieces') this.limitPieces();
      if (target.name === 'size') this.checkSize();
    }

    limitPieces() {
      const boxes = Array.from(this.form?.querySelectorAll('input[name="pieces"]') || []);
      const picked = boxes.filter((b) => b.checked).length;
      boxes.forEach((b) => {
        b.disabled = !b.checked && picked >= this.max;
        b.closest('.amf__piece')?.classList.toggle('is-off', b.disabled);
      });
    }

    size() {
      return this.form?.querySelector('input[name="size"]:checked')?.value || '';
    }

    checkSize() {
      const size = this.size();
      const open = this.slots ? this.slots.sizes || [] : null;
      const blocked = !!size && !!open && open.indexOf(size) === -1;
      if (this.noSize) this.noSize.hidden = !blocked;
      if (this.later && this.slots) this.later.hidden = blocked;
    }

    say(text, isError) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('is-error', !!isError);
    }

    async submit(event) {
      event.preventDefault();
      if (!this.form || this.sending) return;
      const data = new FormData(this.form);
      const pieces = data.getAll('pieces').map(String);
      const size = String(data.get('size') || '');
      const firstName = String(data.get('firstName') || '').trim();
      const email = String(data.get('email') || '').trim();
      const phone = String(data.get('phone') || '').trim();
      if (!pieces.length) return this.fail('Pick at least one piece to try on.', 'input[name="pieces"]');
      if (!size) return this.fail('Pick your size.', 'input[name="size"]');
      if (this.slots && (this.slots.sizes || []).indexOf(size) === -1) return this.checkSize();
      if (!this.day) return this.fail('Pick a day.', '.amf__day:not([disabled])');
      if (!this.time) return this.fail('Pick a time.', 'input[name="time"]:not([disabled])');
      if (!firstName) return this.fail('Add your first name.', 'input[name="firstName"]');
      if (!EMAIL_RE.test(email)) return this.fail('Check your email address. We send the confirmation there.', 'input[name="email"]');
      if (phone && !PHONE_RE.test(phone)) return this.fail('That phone number doesn’t look right. Use numbers only, or leave it out.', 'input[name="phone"]');

      const fitting = {
        pieces,
        size,
        date: this.day,
        time: this.time,
        firstName,
        lastName: String(data.get('lastName') || '').trim(),
        email,
        phone,
        occasion: String(data.get('occasion') || '').trim(),
        notes: String(data.get('notes') || '').trim(),
        website: String(data.get('website') || ''),
        opened: Date.now() - this.openedAt
      };
      this.sending = true;
      if (this.sendButton) this.sendButton.disabled = true;
      this.say('Sending…');
      try {
        const response = await fetch(this.dataset.api, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify({ action: 'fittingRequest', fitting }),
          credentials: 'omit',
          signal: this.controller.signal
        });
        const result = await response.json();
        if (result && result.ok) return this.done(fitting);
        const code = result && result.error;
        if (code === 'taken' || code === 'day') {
          this.time = code === 'taken' ? '' : this.time;
          this.day = code === 'day' ? '' : this.day;
          await this.load();
          this.fail(code === 'taken' ? 'Someone has just booked that time. Pick another one.' : 'That day isn’t open any more. Pick another one.');
        } else if (code === 'size') {
          await this.load();
          this.checkSize();
          this.say('');
        } else if (code === 'email') {
          this.fail('Check your email address. We send the confirmation there.', 'input[name="email"]');
        } else if (code === 'phone') {
          this.fail('That phone number doesn’t look right. Use numbers only, or leave it out.', 'input[name="phone"]');
        } else if (code === 'busy') {
          this.fail('Lots of requests at the moment. Try again in a few minutes.');
        } else {
          this.fail('Something in that wasn’t right. Check it and try again.');
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.fail('That didn’t go through. Check your connection and try again.');
      } finally {
        this.sending = false;
        if (this.sendButton) this.sendButton.disabled = false;
      }
    }

    fail(text, focus) {
      this.say(text, true);
      if (focus) this.form?.querySelector(focus)?.focus();
    }

    done(fitting) {
      const thanks = this.querySelector('[data-thanks]');
      const title = this.querySelector('[data-thanks-title]');
      const text = this.querySelector('[data-thanks-text]');
      if (title) title.textContent = `Thank you, ${fitting.firstName}.`;
      if (text) text.textContent = `We have your request for ${dayText(fitting.date)} at ${fitting.time}. Dana or Cam will confirm it by email, usually within one business day.`;
      this.form.hidden = true;
      if (thanks) {
        thanks.hidden = false;
        thanks.focus();
        thanks.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  customElements.define('amani-fitting', AmaniFitting);
})();
